import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import StripePayment from '../components/Payment/StripePayment';
import SubscriptionManager from '../components/Payment/SubscriptionManager';
import PayPalPayment from '../components/Payment/PayPalPayment';
import { FaShieldAlt, FaTimes, FaCheck } from 'react-icons/fa';

interface Plan {
  id: string;
  name: string;
  price: number;
  period: 'monthly' | 'yearly';
  features: string[];
  popular?: boolean;
  savings?: number;
}

const Subscription: React.FC = () => {
  const { user } = useAuth();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [showPayment, setShowPayment] = useState(false);

  const [paymentMethod, setPaymentMethod] = useState<'stripe' | 'paypal'>('stripe');

  const handleSubscribe = (plan: Plan) => {
    setSelectedPlan(plan);
    setShowPayment(true);
  };

  const handlePaymentSuccess = (paymentData: any) => {
    setShowPayment(false);
    setSelectedPlan(null);
    // You might want to refresh user data or show success message
    alert('Payment successful! Your subscription is now active.');
  };

  const handlePaymentError = (error: string) => {
    console.error('Payment error:', error);
    alert(`Payment failed: ${error}`);
  };

  const formatPrice = (price: number) => {
    return `$${(price / 100).toFixed(2)}`;
  };

  const plans: Plan[] = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      period: billingPeriod,
      features: [
        '100 Persian words',
        'Basic flashcards',
        'Simple quizzes',
        'Basic progress tracking',
        'Word of the day'
      ]
    },
    {
      id: 'premium',
      name: 'Premium',
      price: billingPeriod === 'monthly' ? 999 : 9990,
      period: billingPeriod,
      features: [
        '10,000+ Persian words',
        'Smart flashcards',
        'Unlimited quizzes',
        'Advanced progress tracking',
        'Word of the day + examples',
        'Audio pronunciation',
        'Achievements & gamification',
        'Priority support'
      ],
      popular: true,
      savings: billingPeriod === 'yearly' ? 20 : 0
    },
    {
      id: 'platinum',
      name: 'Platinum',
      price: billingPeriod === 'monthly' ? 2499 : 24990,
      period: billingPeriod,
      features: [
        'All Premium features',
        'Unlimited words',
        'Personalized quizzes',
        'Advanced reports',
        'Private classes',
        'Personal consultation',
        'Early access to new features',
        '24/7 support'
      ],
      savings: billingPeriod === 'yearly' ? 25 : 0
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Cancel Anytime Policy Banner */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 mb-8 shadow-lg">
          <div className="flex flex-col sm:flex-row items-center justify-center text-center sm:text-left">
            <div className="flex-shrink-0 mb-4 sm:mb-0 sm:mr-4">
              <FaShieldAlt className="text-white text-3xl" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white mb-2">
                Cancel Anytime, No Questions Asked
              </h2>
              <p className="text-green-100 text-sm sm:text-base">
                Your subscription can be cancelled at any time. You'll continue to have access until the end of your current billing period.
              </p>
            </div>
            <div className="flex-shrink-0 mt-4 sm:mt-0 sm:ml-4">
              <div className="bg-white bg-opacity-20 rounded-full p-3">
                <FaCheck className="text-white text-xl" />
              </div>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Choose Your Subscription
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Select the perfect plan for your Persian learning journey
          </p>
        </div>

        {/* Current Plan */}
        {user?.subscription?.type !== 'free' && (
          <div className="bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl p-6 text-white mb-8 shadow-xl">
            <div className="flex flex-col sm:flex-row items-center justify-between">
              <div className="text-center sm:text-left mb-4 sm:mb-0">
                <h2 className="text-xl font-bold mb-2">
                  Your Current Plan: {user?.subscription?.type === 'premium' ? 'Premium' : 'Platinum'}
                </h2>
                <p className="text-green-100">
                  {user?.subscription?.endDate && (
                    `Expires: ${new Date(user.subscription.endDate).toLocaleDateString('en-US')}`
                  )}
                </p>
              </div>
              <div className="text-center">
                <div className="text-3xl mb-2">✅</div>
                <p className="text-sm text-green-100 font-medium">Active</p>
              </div>
            </div>
          </div>
        )}

        {/* Billing Toggle */}
        <div className="flex justify-center mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-1 shadow-lg">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-4 sm:px-6 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                billingPeriod === 'monthly'
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                  : 'text-gray-700 dark:text-gray-300 hover:text-orange-500 dark:hover:text-orange-400'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-4 sm:px-6 py-2 rounded-lg text-sm font-medium transition-all duration-200 relative ${
                billingPeriod === 'yearly'
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                  : 'text-gray-700 dark:text-gray-300 hover:text-orange-500 dark:hover:text-orange-400'
              }`}
            >
              Yearly
              {billingPeriod === 'yearly' && (
                <span className="absolute -top-2 -right-2 px-2 py-1 bg-green-500 text-white text-xs rounded-full animate-pulse">
                  Save
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-12">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 shadow-lg border-2 transition-all duration-300 hover:shadow-xl transform hover:scale-105 ${
                plan.popular
                  ? 'border-orange-500 scale-105'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                    Most Popular
                  </span>
                </div>
              )}

              {plan.savings && plan.savings > 0 && (
                <div className="absolute -top-2 -right-2">
                  <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium animate-bounce">
                    Save {plan.savings}%
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white">
                    {formatPrice(plan.price)}
                  </span>
                  {plan.price > 0 && (
                    <span className="text-gray-600 dark:text-gray-400">
                      /{plan.period === 'monthly' ? 'month' : 'year'}
                    </span>
                  )}
                </div>
                {plan.price > 0 && billingPeriod === 'yearly' && (
                  <p className="text-sm text-green-600 dark:text-green-400">
                    ${(plan.price / 12 / 100).toFixed(2)} per month
                  </p>
                )}
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-500 mr-3 mt-1 text-lg">✓</span>
                    <span className="text-gray-700 dark:text-gray-300 text-sm sm:text-base">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSubscribe(plan)}
                className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
                  plan.popular
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600 shadow-lg'
                    : plan.id === 'free'
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    : 'border-2 border-orange-500 text-orange-500 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20'
                }`}
              >
                {plan.id === 'free' ? 'Start Free' : 'Choose Plan'}
              </button>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 shadow-lg mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Frequently Asked Questions
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <FaShieldAlt className="text-green-500 mr-2" />
                Can I cancel my subscription?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                <strong>Yes, absolutely!</strong> You can cancel your subscription at any time with no questions asked. Your access will continue until the end of your paid period, and you won't be charged again.
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Can I change my subscription?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Yes, you can upgrade or downgrade your subscription at any time. Changes will take effect from the next billing cycle.
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Do yearly plans save money?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Yes, yearly subscriptions include a 20-25% discount compared to monthly plans.
              </p>
            </div>
          </div>
        </div>

        {/* Features Comparison */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Feature Comparison
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-4 px-4 font-semibold text-gray-900 dark:text-white">Feature</th>
                  <th className="text-center py-4 px-4 font-semibold text-gray-900 dark:text-white">Free</th>
                  <th className="text-center py-4 px-4 font-semibold text-gray-900 dark:text-white">Premium</th>
                  <th className="text-center py-4 px-4 font-semibold text-gray-900 dark:text-white">Pro</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="py-4 px-4 text-gray-700 dark:text-gray-300">Number of words</td>
                  <td className="text-center py-4 px-4">100</td>
                  <td className="text-center py-4 px-4">10,000+</td>
                  <td className="text-center py-4 px-4">Unlimited</td>
                </tr>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="py-4 px-4 text-gray-700 dark:text-gray-300">Smart flashcards</td>
                  <td className="text-center py-4 px-4">❌</td>
                  <td className="text-center py-4 px-4">✅</td>
                  <td className="text-center py-4 px-4">✅</td>
                </tr>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="py-4 px-4 text-gray-700 dark:text-gray-300">Audio pronunciation</td>
                  <td className="text-center py-4 px-4">❌</td>
                  <td className="text-center py-4 px-4">✅</td>
                  <td className="text-center py-4 px-4">✅</td>
                </tr>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="py-4 px-4 text-gray-700 dark:text-gray-300">Advanced reports</td>
                  <td className="text-center py-4 px-4">❌</td>
                  <td className="text-center py-4 px-4">❌</td>
                  <td className="text-center py-4 px-4">✅</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-gray-700 dark:text-gray-300">Support</td>
                  <td className="text-center py-4 px-4">Email</td>
                  <td className="text-center py-4 px-4">Chat + Email</td>
                  <td className="text-center py-4 px-4">24/7 Phone</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Payment Modal */}
        {showPayment && selectedPlan && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Complete Payment</h3>
                <button
                  onClick={() => setShowPayment(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
              
              {/* Plan Summary - More Prominent */}
              <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl p-6 mb-6">
                <div className="text-center">
                  <h4 className="text-xl font-bold text-gray-900 mb-2">
                    {selectedPlan.name} Plan
                  </h4>
                  <div className="text-4xl font-bold text-orange-600 mb-2">
                    {formatPrice(selectedPlan.price)}
                  </div>
                  <p className="text-gray-600">
                    {selectedPlan.period === 'monthly' ? 'Monthly billing' : 'Yearly billing'}
                    {selectedPlan.savings !== undefined && selectedPlan.savings > 0 ? (
                      <span className="ml-2 text-green-600 font-semibold">
                        (Save {selectedPlan.savings}%)
                      </span>
                    ) : null}
                  </p>
                  {selectedPlan.period === 'yearly' && selectedPlan.price > 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      ${(selectedPlan.price / 12 / 100).toFixed(2)} per month
                    </p>
                  )}
                </div>
              </div>

              {/* Payment Method Selection */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Choose Payment Method</h4>
                <div className="flex gap-3">
                  <button
                    className={`flex-1 py-3 px-4 rounded-lg font-semibold border-2 transition-all ${
                      paymentMethod === 'stripe' 
                        ? 'border-blue-600 bg-blue-600 text-white' 
                        : 'border-gray-300 bg-white text-gray-700 hover:border-blue-400'
                    }`}
                    onClick={() => setPaymentMethod('stripe')}
                  >
                    💳 Pay with Card
                  </button>
                  <button
                    className={`flex-1 py-3 px-4 rounded-lg font-semibold border-2 transition-all ${
                      paymentMethod === 'paypal' 
                        ? 'border-yellow-400 bg-yellow-400 text-white' 
                        : 'border-gray-300 bg-white text-gray-700 hover:border-yellow-400'
                    }`}
                    onClick={() => setPaymentMethod('paypal')}
                  >
                    🅿️ Pay with PayPal
                  </button>
                </div>
              </div>

              {/* Payment Form */}
              <div className="mb-6">
                {paymentMethod === 'stripe' && (
                  <StripePayment
                    priceId={selectedPlan.id}
                    amount={selectedPlan.price}
                    isSubscription={true}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                  />
                )}
                {paymentMethod === 'paypal' && (
                  <PayPalPayment
                    planId="P-PLACEHOLDER123"
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                  />
                )}
              </div>

              {/* Cancel Anytime Policy in Payment Modal */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                <div className="flex items-start">
                  <FaShieldAlt className="text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-semibold text-green-800 mb-1">
                      Cancel Anytime Policy
                    </h4>
                    <p className="text-xs text-green-700">
                      You can cancel your subscription at any time. Your access will continue until the end of your current billing period. No questions asked, no hassle.
                    </p>
                  </div>
                </div>
              </div>

              {/* Cancel Button */}
              <button
                onClick={() => setShowPayment(false)}
                className="w-full py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Subscription Management */}
        {user && (
          <div className="mt-12">
            <SubscriptionManager />
          </div>
        )}
      </div>
    </div>
  );
};

export default Subscription; 